<template>
  <div class="customize-list">
    <h3 v-if="sectionTitle" class="customize-list__header" v-html="sectionTitle" />
    <div v-if="sectionTitle" class="customize-list__subtitle-block">
      <div
        class="customize-list__fields-disclaimer"
        v-html="$t('product.admetox.requiredFieldsDisclaimer')"
      />
      <app-button
        class="customize-list__reset-btn"
        variant="secondary"
        type="button"
        size="sub-small"
        :text="$t('common.clearAll')"
        @clicked="$emit('resetAllCustomizationsRequested')"
      />
    </div>
    <div v-if="sectionHeader" class="customize-list__section-header">
      <h3 class="text-h3 text-color-navy" v-html="sectionHeader.title" />
      <p class="text-description" v-html="sectionHeader.subtitle" />
    </div>
    <div class="customize-list__items-container">
      <div v-for="(group, groupIdx) in groups" :key="groupIdx" class="customize-list__items-group">
        <div v-if="!group.isDefault" class="customize-list__items-group--header">
          <div
            class="customize-list__items-group--title-wrapper"
            @click="onGroupToggle(group, groupIdx)"
          >
            <h4
              class="customize-list__items-group--title"
              :class="{ 'customize-list__items-group--title-clickable': group.isCollapsible }"
            >
              {{ group.name }}
            </h4>
            <span
              v-if="group.isCollapsible"
              :class="{ 'checkbox-subitem--active': !group.collapsed }"
              class="checkbox-subitem"
            ></span>
          </div>
          <p v-if="group.description" class="customize-list__items-group--desc">
            {{ group.description }}
          </p>
        </div>
        <div v-if="group.isDefault || !group.collapsed">
          <div
            v-for="(item, customizationIdx) in group.customizations"
            :key="`${item.title}-${customizationIdx}`"
            :class="{
              'customize-list__bordered': !group.isDefault,
              'customize-list__nested': item.nested,
            }"
            class="customize-list__item-wrapper customize-list__item-wrapper--inline"
          >
            <div class="customize-list__title-wrapper">
              <div class="customize-list__item-title-container">
                <h4 class="customize-list__item-title">
                  {{ item.title }}
                  <span v-if="item.required" class="customize-list__required">*</span>
                </h4>
                <div v-if="item.info" class="customize-list__info-icon">
                  ?
                  <span class="customize-list__info-text" v-html="item.info" />
                </div>
                <button
                  v-if="item.textarea"
                  :class="{
                    'customize-list__reset--img-selector': item.iconGroup,
                  }"
                  class="customize-list__reset-textarea button button--sub-small button--secondary"
                  @click="$emit('resetSingleAttributeRequested', item)"
                  v-html="$t('common.clear')"
                />
              </div>
              <p
                v-if="item.subtitle"
                class="customize-list__item-subtitle"
                v-html="item.subtitle"
              />
            </div>
            <div class="customize-list__inner-wrapper">
              <div v-if="item.widgetType === 'qty'" class="customize-list__qty-wrapper">
                <quantity-input
                  :modelValue="item.selected"
                  :min="item.minValue"
                  :max="item.maxValue"
                  :step="item.increment"
                  :class="{
                    'customize-list--fail': qtyValidation(validations[customizationIdx].selected),
                    'customize-list--invalid':
                      isValidationRun &&
                      qtyValidation(validations[customizationIdx].selected) &&
                      areFieldsHighlighted,
                  }"
                  @update:modelValue="onValueChanged(item, $event)"
                />
              </div>
              <div
                v-if="!validations[customizationIdx].selected.minValue"
                class="customize-list__error"
              >
                Min value: {{ item.minValue }}
              </div>
              <div
                v-if="!validations[customizationIdx].selected.maxValue"
                class="customize-list__error"
              >
                Max value: {{ item.maxValue }}
              </div>
              <div
                v-if="!validations[customizationIdx].selected.isPositiveInteger"
                class="customize-list__error"
                v-html="$t('product.admetox.posIntegerError')"
              ></div>
              <div
                v-if="!validations[customizationIdx].selected.isIncrement"
                class="customize-list__error"
                v-html="
                  $t('product.admetox.incrementError', {
                    incrementAmount: item.increment,
                  })
                "
              ></div>
              <multiselect
                v-if="item.widgetType === 'dropList'"
                :modelValue="item.selected"
                :options="item.options"
                :disabled="item.options.length === 1"
                :showLabels="false"
                :allowEmpty="false"
                :searchable="false"
                :preselectFirst="item.widgetType === 'qty'"
                openDirection="bottom"
                placeholder="Select an Option"
                :class="{
                  'multiselect--orange':
                    isValidationRun && !validations[customizationIdx].selected.required,
                  'multiselect--invalid':
                    isValidationRun &&
                    !validations[customizationIdx].selected.required &&
                    areFieldsHighlighted,
                  'customize-list__select-selected': item.selected,
                }"
                class="multiselect--small customize-list__select"
                @update:modelValue="onValueChanged(item, $event)"
              />
              <div v-if="item.widgetType === 'chips'" class="customize-list__chip-container">
                <button
                  v-for="(option, optionIdx) in item.options"
                  :key="`${option}-${optionIdx}`"
                  class="customize-list__chip"
                  :class="{
                    'customize-list__chip-selected': item.selected === option,
                    'customize-list--fail':
                      isValidationRun && !validations[customizationIdx].selected.required,
                    'customize-list__chip--invalid':
                      isValidationRun &&
                      !validations[customizationIdx].selected.required &&
                      areFieldsHighlighted,
                  }"
                  @click="onValueChanged(item, option)"
                >
                  {{ option }}
                </button>
              </div>
              <textarea
                v-if="item.widgetType === 'textarea'"
                maxlength="255"
                rows="10"
                class="form-textarea form-textarea--small customize-list__textarea"
                :class="{
                  'customize-list--fail':
                    (isValidationRun && !validations[customizationIdx].selected.required) ||
                    hasNumericError(item) ||
                    hasComparisonError(item),
                  'customize-list__textarea--numeric': item.isNumericFreeText,
                  'customize-list--invalid':
                    isValidationRun &&
                    areFieldsHighlighted &&
                    (!validations[customizationIdx].selected.required ||
                      hasNumericError(item) ||
                      hasComparisonError(item)),
                  'customize-list__textarea-selected': item.selected,
                }"
                :value="item.selected"
                :placeholder="item.textarea.placeholder"
                @input="onValueChanged(item, $event.target.value)"
              />
              <div
                v-if="item.widgetType === 'textarea' && item.selected && !item.isNumericFreeText"
                class="customize-list__textareacounter"
                v-text="item.selected.length + '/255'"
              />
              <div
                v-if="hasNumericError(item)"
                class="customize-list__error"
                v-html="
                  isCellProduct
                    ? $t('product.admetox.numericCellError')
                    : $t('product.admetox.numericVolumeError')
                "
              />
              <div
                v-if="
                  hasComparisonError(item) && !isSomeChildCustomizationVisible(item, customizations)
                "
                class="customize-list__error"
                v-html="$t('product.admetox.invalidVolumeError')"
              />
              <div v-if="item.widgetType === 'iconGroup'" class="customize-list__icon-group">
                <span
                  v-for="icon in item.iconGroup"
                  :key="icon.title"
                  class="customize-list__icon-wrapper"
                >
                  <div
                    v-if="item.selected === icon.title"
                    class="customize-list__item-descr"
                    v-html="icon.title"
                  />
                  <button
                    v-if="icon.img"
                    :class="{
                      'customize-list__icon-item-wrapper--active': item.selected === icon.title,
                      'customize-list--fail':
                        isValidationRun && !validations[customizationIdx].selected.required,
                      'customize-list--invalid':
                        isValidationRun &&
                        !validations[customizationIdx].selected.required &&
                        areFieldsHighlighted,
                    }"
                    class="customize-list__icon-item-wrapper"
                    @click="onValueChanged(item, icon.title)"
                  >
                    <img :src="icon.img" :alt="icon.imgAlt" class="customize-list__icon" />
                  </button>
                </span>
              </div>
              <div v-if="item.widgetType === 'cards'" class="customize-list__card-container">
                <div
                  v-for="(card, n) in item.cardsOptions"
                  :key="`${card.title}-${n}`"
                  class="customize-list__card"
                  :class="{
                    'customize-list__card-selected': item.selected === card,
                    'customize-list--fail':
                      isValidationRun && !validations[customizationIdx].selected.required,
                    'customize-list__card--invalid':
                      isValidationRun &&
                      !validations[customizationIdx].selected.required &&
                      areFieldsHighlighted,
                  }"
                  @click="onValueChanged(item, card)"
                >
                  <h4 class="customize-list__card-title">
                    {{ card.title }}
                  </h4>
                  <p class="customize-list__card-subtitle">
                    {{ card.subtitle }}
                  </p>
                  <table class="customize-list__card-table">
                    <tr class="customize-list__card-table--header">
                      <th v-if="card.columnTitleLeft">
                        {{ card.columnTitleLeft }}
                      </th>
                      <th v-if="card.columnTitleRight">
                        {{ card.columnTitleRight }}
                      </th>
                    </tr>
                    <tr class="customize-list__card-table--body">
                      <td v-if="card.columnDataLeft">
                        <ul>
                          <li
                            v-for="(columnDataLeftItem, columnDataLeftIdx) in card.columnDataLeft"
                            :key="columnDataLeftIdx"
                          >
                            <span>{{ columnDataLeftItem }}</span>
                          </li>
                        </ul>
                      </td>
                      <td v-if="card.columnDataRight">
                        <ul>
                          <li
                            v-for="(
                              columnDataRightItem, columnDataRightIdx
                            ) in card.columnDataRight"
                            :key="columnDataRightIdx"
                          >
                            <span>{{ columnDataRightItem }}</span>
                          </li>
                        </ul>
                      </td>
                    </tr>
                  </table>
                </div>
              </div>
              <div
                v-if="item.widgetType === 'dropList'"
                :class="{
                  'customize-list__reset--img-selector': item.iconGroup,
                }"
                class="customize-list__reset"
                @click="$emit('resetSingleAttributeRequested', item)"
                v-html="$t('common.clear')"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import QuantityInput from '@/components/common/QuantityInput.vue'

export default {
  name: 'ProductDetailsCustomizations',
  components: {
    QuantityInput,
  },
  props: {
    sectionTitle: {
      type: String,
      required: false,
      default: '',
    },
    sectionHeader: {
      type: Object,
      required: false,
      default: () => {},
    },
    groupList: {
      type: Array,
      required: true,
    },
    customizations: {
      type: Array,
      required: true,
    },
    validations: {
      type: Array,
      required: true,
    },
    isCellProduct: {
      type: Boolean,
      required: true,
    },
    isValidationRun: {
      type: Boolean,
      required: true,
    },
    areFieldsHighlighted: {
      type: Boolean,
      required: true,
    },
    hasNumericError: {
      type: Function,
      required: true,
    },
    hasComparisonError: {
      type: Function,
      required: true,
    },
    isSomeChildCustomizationVisible: {
      type: Function,
      required: true,
    },
  },
  emits: [
    'valueChanged',
    'resetAllCustomizationsRequested',
    'resetSingleAttributeRequested',
    'toggleGroup',
  ],
  computed: {
    groups() {
      return (
        this.groupList.map((groupItem) => {
          const customizations = {}
          this.customizations.forEach((customization, idx) => {
            if (
              (groupItem.isDefault && !customization.groupName) ||
              (!groupItem.isDefault && groupItem.name === customization.groupName)
            ) {
              customizations[idx] = customization
            }
          })
          const isCollapsible =
            Object.values(customizations).length &&
            Object.values(customizations).every((el) => !el.required)
          return {
            ...groupItem,
            customizations,
            collapsed: isCollapsible ? groupItem.collapsed : false,
            isCollapsible,
          }
        }) || []
      )
    },
  },
  methods: {
    onValueChanged(customization, value) {
      this.$emit('valueChanged', { customization, value })
    },
    qtyValidation(validationField) {
      return [
        validationField.isPositiveInteger,
        validationField.minValue,
        validationField.maxValue,
        validationField.isIncrement,
      ].some((el) => !el)
    },
    onGroupToggle(group, groupIdx) {
      if (group.isCollapsible) this.$emit('toggleGroup', groupIdx)
    },
  },
}
</script>
